import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Input,
    VStack,
    Heading,
    Text,
    SimpleGrid,
    Select as ChakraSelect,
    Checkbox,
    Textarea,
    useToast,
    Icon,
    Flex,
    Badge,
    IconButton,
    HStack,
    FormErrorMessage,
    ListItem,
    Image,
    Divider
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaUpload, FaTrash, FaCheckCircle, FaExclamationCircle, FaPlus } from 'react-icons/fa';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import Select from 'react-select';
import axiosInstance from '../services/axiosInstance';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const RecruitmentFormPage = () => {
    const toast = useToast();
    const [files, setFiles] = useState([]);
    const primaryColor = '#F2C919';
    const secondaryColor = '#2C3E50';

    const { register, handleSubmit, control, formState: { errors, isSubmitting }, reset, watch } = useForm({
        defaultValues: {
            institutions: [{ name: "" }],
            subjects: [],
            levels: [],
            levelSections: {} // Structured as { levelValue: [selectedOptions] }
        }
    });

    const selectedLevels = watch("levels");

    const { fields, append, remove } = useFieldArray({
        control,
        name: "institutions"
    });

    const tunisianStates = [
        "أريانة", "باجة", "بن عروس", "بنزرت", "قابس", "قفصة", "جندوبة", "القيروان",
        "القصرين", "قبلي", "الكاف", "المهدية", "منوبة", "مدنين", "المنستير", "نابل",
        "صفاقس", "سيدي بوزيد", "سليانة", "سوسة", "تطاوين", "توزر", "تونس", "زغوان"
    ];

    const subjectOptions = [
        { value: 'اللغة العربية', label: 'اللغة العربية' },
        { value: 'اللغة الفرنسية', label: 'اللغة الفرنسية' },
        { value: 'اللغة الإنجليزية', label: 'اللغة الإنجليزية' },
        { value: 'اللغة التركية', label: 'اللغة التركية' },
        { value: 'اللغة الألمانية', label: 'اللغة الألمانية' },
        { value: 'اللغة الإسبانية', label: 'اللغة الإسبانية' },
        { value: 'الرياضيات', label: 'الرياضيات' },
        { value: 'الفيزياء', label: 'الفيزياء' },
        { value: 'الكيمياء', label: 'الكيمياء' },
        { value: 'علوم الحياة والأرض', label: 'علوم الحياة والأرض' },
        { value: 'التاريخ', label: 'التاريخ' },
        { value: 'الجغرافيا', label: 'الجغرافيا' },
        { value: 'الفلسفة', label: 'الفلسفة' },
        { value: 'التربية المدنية', label: 'التربية المدنية' },
        { value: 'التربية الإسلامية', label: 'التربية الإسلامية' },
        { value: 'الإعلامية / علوم الحاسوب / البرمجة', label: 'الإعلامية / علوم الحاسوب / البرمجة' },
        { value: 'الاقتصاد', label: 'الاقتصاد' },
        { value: 'المحاسبة', label: 'المحاسبة' },
        { value: 'التربية التكنولوجية', label: 'التربية التكنولوجية' },
        { value: 'التربية الفنية / الموسيقية', label: 'التربية الفنية / الموسيقية' },
        { value: 'الكهرباء', label: 'الكهرباء (Electric)' },
    ];

    const levelOptions = [
        { value: '7', label: 'السابعة أساسي' },
        { value: '8', label: 'الثامنة أساسي' },
        { value: '9', label: 'التاسعة أساسي' },
        { value: '1', label: 'الأولى ثانوي' },
        { value: '2', label: 'الثانية ثانوي' },
        { value: '3', label: 'الثالثة ثانوي' },
        { value: 'BAC', label: 'باكالوريا' },
    ];

    const sectionOptions = [
        { value: 'الأداب', label: 'الأداب' },
        { value: 'اقتصاد وتصرف', label: 'اقتصاد وتصرف' },
        { value: 'تقنية', label: 'تقنية' },
        { value: 'علوم', label: 'علوم' },
        { value: 'رياضيات', label: 'رياضيات' },
        { value: 'اعلامية', label: 'اعلامية' },
        { value: 'رياضة', label: 'رياضة' },
    ];

    const selectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: 'var(--chakra-radii-xl)',
            padding: '4px',
            borderColor: 'var(--chakra-colors-gray-200)',
            fontFamily: 'Tajawal, sans-serif'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: primaryColor,
            borderRadius: '8px',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: secondaryColor,
            fontWeight: 'bold'
        })
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (files.length + selectedFiles.length > 5) {
            toast({
                title: "خطأ",
                description: "يمكنك رفع 5 ملفات كحد أقصى",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        setFiles([...files, ...selectedFiles]);
    };

    const removeFile = (index) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const onSubmit = async (data) => {
        if (files.length === 0) {
            toast({
                title: "وثائق مطلوبة",
                description: "يرجى رفع نسخة من الشهادة العلمية أو ما يثبت مستواك التعليمي.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        try {
            const formData = new FormData();
            // Regular fields
            formData.append('fullName', data.fullName);
            formData.append('phone', data.phone);
            formData.append('state', data.state);
            formData.append('educationLevel', data.educationLevel);
            formData.append('experience', data.experience);
            formData.append('hasTablet', data.hasTablet);
            formData.append('hasPDF', data.hasPDF);

            // Arrays
            data.institutions.forEach(inst => formData.append('institution', inst.name));
            data.subjects.forEach(sub => formData.append('subjects', sub.value));
            data.levels.forEach(lvl => formData.append('levels', lvl.value));

            if (data.levelSections) {
                const structuredSections = Object.entries(data.levelSections)
                    .filter(([level, sections]) => sections && sections.length > 0)
                    .map(([level, sections]) => ({
                        level: level,
                        sections: sections.map(s => s.value)
                    }));

                if (structuredSections.length > 0) {
                    formData.append('sections', JSON.stringify(structuredSections));
                }
            }

            files.forEach(file => {
                formData.append('documents', file);
            });

            const response = await axiosInstance.post('/recruitment', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                toast({
                    title: "تم الإرسال بنجاح",
                    description: "شكراً لاهتمامك بالانضمام إلى COBRA Education. سنتواصل معك قريباً.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                    position: "top"
                });
                reset();
                setFiles([]);
            }
        } catch (error) {
            toast({
                title: "خطأ في الإرسال",
                description: error.response?.data?.message || "حدث خطأ أثناء إرسال طلبك. يرجى المحاولة لاحقاً.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <Box dir="rtl" bg="gray.50" minH="100vh">
            <Header />

            <Box
                bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`}
                color="white"
                py={{ base: 12, md: 20 }}
                position="relative"
            >
                <Container maxW="1400px" textAlign="center">
                    <VStack spacing={4}>
                        <Badge
                            bg={primaryColor}
                            color={secondaryColor}
                            px={4}
                            py={1}
                            borderRadius="full"
                            fontSize="sm"
                            fontWeight="bold"
                        >
                            انتداب رسمي
                        </Badge>
                        <Heading fontSize={{ base: '2xl', md: '4xl' }} fontFamily="Cairo, sans-serif" fontWeight="900">
                             كل أستاذ يتم انتدابه سينخرط كإطار رسمي ضمن COBRA Education
                        </Heading>
                    </VStack>
                </Container>
            </Box>

                <Box maxW="800px" mx="auto" py={12} mt={-16}>

                    {/* Right Side: The Form */}
                    <MotionBox
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        bg="white"
                        p={{ base: 6, md: 10 }}
                        borderRadius="3xl"
                        boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                        border="1px solid"
                        borderColor="gray.100"
                    >
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* ... Form Content Remains the same ... */}
                            <VStack spacing={8} align="stretch">
                                <Heading size="md" color={secondaryColor} borderBottom="2px solid" borderColor={primaryColor} pb={2} fontFamily="Cairo, sans-serif">
                                    المعلومات الشخصية والمهنية
                                </Heading>
                                {/* Form fields go here */}
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                    <FormControl isRequired isInvalid={errors.fullName}>
                                        <FormLabel fontWeight="600">الاسم واللقب</FormLabel>
                                        <Input
                                            {...register('fullName', { required: 'هذا الحقل إلزامي' })}
                                            placeholder="الاسم واللقب الكامل"
                                            size="lg"
                                            borderRadius="xl"
                                        />
                                        <FormErrorMessage>{errors.fullName?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isRequired isInvalid={errors.phone}>
                                        <FormLabel fontWeight="600">رقم الهاتف</FormLabel>
                                        <Input
                                            {...register('phone', { required: 'هذا الحقل إلزامي' })}
                                            placeholder="رقم الهاتف"
                                            size="lg"
                                            borderRadius="xl"
                                        />
                                        <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isRequired isInvalid={errors.state}>
                                        <FormLabel fontWeight="600">الولاية</FormLabel>
                                        <ChakraSelect
                                            {...register('state', { required: 'يرجى اختيار الولاية' })}
                                            placeholder="اختر الولاية"
                                            size="lg"
                                            borderRadius="xl"
                                        >
                                            {tunisianStates.map(state => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </ChakraSelect>
                                        <FormErrorMessage>{errors.state?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isRequired isInvalid={errors.educationLevel}>
                                        <FormLabel fontWeight="600">المستوى التعليمي (أو الشهادة العلمية)</FormLabel>
                                        <Input
                                            {...register('educationLevel', { required: 'هذا الحقل إلزامي' })}
                                            placeholder="مثال: إجازة، ماجستير، دكتوراة..."
                                            size="lg"
                                            borderRadius="xl"
                                        />
                                        <FormErrorMessage>{errors.educationLevel?.message}</FormErrorMessage>
                                    </FormControl>
                                </SimpleGrid>

                                <FormControl isRequired isInvalid={errors.experience}>
                                    <FormLabel fontWeight="600">الخبرة في التدريس</FormLabel>
                                    <Textarea
                                        {...register('experience', { required: 'هذا الحقل إلزامي' })}
                                        placeholder="يرجى ذكر سنوات الخبرة والمؤسسات التي عملت بها"
                                        size="lg"
                                        borderRadius="xl"
                                        rows={4}
                                    />
                                    <FormErrorMessage>{errors.experience?.message}</FormErrorMessage>
                                </FormControl>

                                <Box>
                                    <FormLabel fontWeight="600">المؤسسات التي زاولت بها الدراسة أو التدريس</FormLabel>
                                    <VStack align="stretch" spacing={3}>
                                        {fields.map((field, index) => (
                                            <HStack key={field.id}>
                                                <Input
                                                    {...register(`institutions.${index}.name`, { required: 'يرجى إدخال اسم المؤسسة' })}
                                                    placeholder={`اسم المؤسسة رقم ${index + 1}`}
                                                    size="lg"
                                                    borderRadius="xl"
                                                />
                                                {fields.length > 1 && (
                                                    <IconButton
                                                        icon={<FaTrash />}
                                                        onClick={() => remove(index)}
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        aria-label="حذف"
                                                    />
                                                )}
                                            </HStack>
                                        ))}
                                        <Button
                                            leftIcon={<FaPlus />}
                                            onClick={() => append({ name: "" })}
                                            variant="outline"
                                            width="fit-content"
                                            size="sm"
                                            colorScheme="blue"
                                            fontSize="xs"
                                        >
                                            إضافة مؤسسة أخرى
                                        </Button>
                                    </VStack>
                                </Box>

                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                    <FormControl isRequired isInvalid={errors.subjects}>
                                        <FormLabel fontWeight="600">المواد التي تدرّسها</FormLabel>
                                        <Controller
                                            control={control}
                                            name="subjects"
                                            rules={{ required: 'يرجى اختيار مادة واحدة على الأقل' }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isMulti
                                                    options={subjectOptions}
                                                    placeholder="اختر المواد..."
                                                    styles={selectStyles}
                                                    noOptionsMessage={() => "لا توجد نتائج"}
                                                />
                                            )}
                                        />
                                        <FormErrorMessage>{errors.subjects?.message}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isRequired isInvalid={errors.levels}>
                                        <FormLabel fontWeight="600">المستويات التعليمية التي تدرّسها</FormLabel>
                                        <Controller
                                            control={control}
                                            name="levels"
                                            rules={{ required: 'يرجى اختيار مستوى واحد على الأقل' }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isMulti
                                                    options={levelOptions}
                                                    placeholder="اختر المستويات..."
                                                    styles={selectStyles}
                                                    noOptionsMessage={() => "لا توجد نتائج"}
                                                />
                                            )}
                                        />
                                        <FormErrorMessage>{errors.levels?.message}</FormErrorMessage>
                                    </FormControl>
                                </SimpleGrid>

                                {selectedLevels && selectedLevels.filter(lvl => ['2', '3', 'BAC'].includes(lvl.value)).map(lvl => (
                                    <MotionBox
                                        key={lvl.value}
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        transition={{ duration: 0.3 }}
                                        mb={4}
                                    >
                                        <FormControl isRequired isInvalid={errors.levelSections?.[lvl.value]}>
                                            <FormLabel fontWeight="600">الشعبة لـ {lvl.label}</FormLabel>
                                            <Controller
                                                control={control}
                                                name={`levelSections.${lvl.value}`}
                                                rules={{ required: 'يرجى اختيار شعبة واحدة على الأقل' }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        isMulti
                                                        options={sectionOptions}
                                                        placeholder={`اختر الشعب لـ ${lvl.label}...`}
                                                        styles={selectStyles}
                                                        noOptionsMessage={() => "لا توجد نتائج"}
                                                    />
                                                )}
                                            />
                                            <FormErrorMessage>{errors.levelSections?.[lvl.value]?.message}</FormErrorMessage>
                                        </FormControl>
                                    </MotionBox>
                                ))}

                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                    <Checkbox {...register('hasTablet')} colorScheme="yellow" iconColor={secondaryColor}>
                                        هل تملك لوحة رقمية (Tablet graphique) ؟
                                    </Checkbox>
                                    <Checkbox {...register('hasPDF')} colorScheme="yellow" iconColor={secondaryColor}>
                                        هل تملك دروس وتمارين بصيغة PDF ؟
                                    </Checkbox>
                                </SimpleGrid>

                                <Box>
                                    <Heading size="md" color={secondaryColor} borderBottom="2px solid" borderColor={primaryColor} pb={2} mb={4} fontFamily="Cairo, sans-serif">
                                        الوثائق الداعمة (إلزامي)
                                    </Heading>
                                    <Text fontSize="sm" color="gray.500" mb={4}>
                                        يرجى رفع نسخة من الشهادة العلمية (Diplôme) أو السيرة الذاتية (بصيغة PDF أو كصور، 5 ملفات كحد أقصى).
                                    </Text>

                                    <VStack align="stretch" spacing={4}>
                                        <Button
                                            as="label"
                                            htmlFor="file-upload"
                                            variant="outline"
                                            border="2px dashed"
                                            borderColor={errors.files ? "red.500" : "gray.300"}
                                            h="100px"
                                            _hover={{ borderColor: primaryColor, bg: 'yellow.50' }}
                                            cursor="pointer"
                                            leftIcon={<Icon as={FaUpload} boxSize={6} />}
                                        >
                                            انقر هنا لرفع الملفات
                                            <input
                                                id="file-upload"
                                                type="file"
                                                multiple
                                                accept=".pdf,image/*"
                                                style={{ display: 'none' }}
                                                onChange={handleFileChange}
                                            />
                                        </Button>

                                        {files.length > 0 ? (
                                            <VStack align="stretch" spacing={2}>
                                                {files.map((file, index) => (
                                                    <HStack key={index} p={3} bg="gray.50" borderRadius="lg" justify="space-between">
                                                        <HStack spacing={3}>
                                                            <Icon as={file.type === 'application/pdf' ? FaCheckCircle : FaUpload} color={primaryColor} />
                                                            <Text fontSize="sm" isTruncated maxW="200px">{file.name}</Text>
                                                            <Badge colorScheme="gray">{(file.size / 1024 / 1024).toFixed(2)} ميغابايت</Badge>
                                                        </HStack>
                                                        <IconButton
                                                            icon={<FaTrash />}
                                                            size="sm"
                                                            colorScheme="red"
                                                            variant="ghost"
                                                            onClick={() => removeFile(index)}
                                                            aria-label="حذف الملف"
                                                        />
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        ) : (
                                            <HStack color="red.500" fontSize="sm">
                                                <Icon as={FaExclamationCircle} />
                                                <Text>يجب تحميل الشهادة العلمية (Diplôme) لإتمام الطلب</Text>
                                            </HStack>
                                        )}
                                    </VStack>
                                </Box>

                                <Button
                                    type="submit"
                                    bg={primaryColor}
                                    color={secondaryColor}
                                    size="xl"
                                    h="60px"
                                    fontSize="xl"
                                    fontWeight="900"
                                    _hover={{ bg: '#e0ba16', transform: 'translateY(-2px)' }}
                                    _active={{ transform: 'translateY(0)' }}
                                    isLoading={isSubmitting}
                                    borderRadius="xl"
                                    boxShadow="lg"
                                >
                                    إرسال طلب الانتداب
                                </Button>
                            </VStack>
                        </form>
                    </MotionBox>
                </Box>
            <Footer />
        </Box>
    );
};

export default RecruitmentFormPage;
